import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {AnnotationService} from '@app/annotation/annotation.service';

import {AssetService, ReviewAsset} from '@app/asset';
import {AuthService} from '@app/auth/auth.service';
import {AssetBounds, InvalidGuid, PresentingState, ReviewMember, ReviewState, Slot, SyncState} from "@app/review/models";
import {ReviewImageService} from '@app/review/review-image.service';
import {ReviewVideoService, Source} from '@app/review/review-video.service';
import {StageService} from "@app/review/stage.service";
import {deserialize} from '@app/serializable';
import {User} from '@app/shared/user.service';
import {BaseMessage, MessageType, WebSocketService} from '@app/websocket/websocket.service';

import * as Sentry from "@sentry/angular";
import {BehaviorSubject, Observable, of, ReplaySubject, Subject, switchMap} from 'rxjs';
import {filter, map, tap} from 'rxjs/operators';


@Injectable({
    providedIn: 'root',
})
export class ReviewService {
    private _user: User;
    private _reviewState: ReviewState;
    private _reviewId: string;
    private _review: ReviewAsset;
    private _userMap: Record<string, ReviewMember>;
    // Ids of not-ready children (e.g. still-transcoding video) this client is currently subscribed to via
    // AssetSubscription, so completion is pushed to every viewer, not just whoever started the upload.
    private _watchedAssetIds: Set<string> = new Set<string>();

    // Subjects
    private reviewStateSubject = new ReplaySubject<ReviewState>();
    private reviewSubject = new Subject<ReviewAsset>();
    private syncingStateSubject = new BehaviorSubject<SyncState>(SyncState.None);
    private presenterStateSubject = new BehaviorSubject<PresentingState>(PresentingState.None);
    private assetBoundsSubject = new BehaviorSubject<AssetBounds>(new AssetBounds());
    // Asset ids whose processing has errored out (VideoQueue Status.Error) - frog-ws stops polling them the
    // moment that happens, so this is the only remaining record of the failure on the client.
    private failedAssetIdsSubject = new BehaviorSubject<Set<string>>(new Set<string>());

    // Observables
    public reviewState$ = this.reviewStateSubject.asObservable();
    public review$ = this.reviewSubject.asObservable();
    public syncingState$ = this.syncingStateSubject.asObservable();
    public presenterState$ = this.presenterStateSubject.asObservable();
    public assetBounds$ = this.assetBoundsSubject.asObservable();
    public failedAssetIds$ = this.failedAssetIdsSubject.asObservable();

    constructor(
        private websocketService: WebSocketService,
        private reviewVideoService: ReviewVideoService,
        private reviewImageService: ReviewImageService,
        private assetService: AssetService,
        private annotationService: AnnotationService,
        private stageService: StageService,
        private authService: AuthService,
        private http: HttpClient,
    ) {
        this._reviewState = new ReviewState();
        this._reviewState.IsLooping = true;
        this._userMap = {};

        this.authService.user
            .pipe(filter((i) => Boolean(i)))
            .subscribe((user) => (this._user = user));

        this.assetService.assetUpdated.subscribe((updatedAsset) => {
            if (this._review) {
                const index = this._review.children.findIndex((a) => a.id === updatedAsset.id);
                if (index !== -1) {
                    this._review.children[index] = updatedAsset;
                    this.reviewSubject.next(this._review);
                }
            }
        });

        this.websocketService.message$
            .pipe(
                filter(
                    (message) =>
                        message.Type === MessageType.ReviewState ||
                        message.Type === MessageType.AnnotationChange ||
                        message.Type === MessageType.UserCursor ||
                        message.Type === MessageType.ReviewChange ||
                        message.Type === MessageType.AssetSubscription,
                ),
            )
            .subscribe((message) => this.webSocketMessage(message));
    }

    public getReview(reviewId: string): Observable<ReviewAsset> {
        this._reviewId = reviewId;

        return this.http.get<ReviewAsset>(`/api/review/${reviewId}/`)
            .pipe(
                switchMap(review => {
                    if (review.child_ids.length === 0) {
                        review.children = [];
                        return of(review);
                    }

                    return this.assetService.resolve(review.child_ids)
                        .pipe(
                            map(assets => {
                                review.children = [];
                                for (const asset of assets) {
                                    const index = review.child_ids.indexOf(asset.id);
                                    review.children[index] = asset;
                                }
                                return review;
                            })
                        )
                }),
                tap(review => {
                    this._review = review;
                    this.reviewSubject.next(review);
                    this.reconcileAssetWatches(review);
                })
            );
    }

    public createReview(
        streamId: string,
        name: string,
        description: string,
    ): Observable<ReviewAsset> {
        const url = `/api/review/`;
        const options = {
            name,
            description,
            stream_id: streamId,
            withCredentials: true,
        };
        return this.http.post<ReviewAsset>(url, options).pipe(
            map((obj) => deserialize(ReviewAsset, obj)),
            tap(() => {
                this.assetService.filter(true);
            }),
        );
    }

    public removeAsset(
        assetId: string,
        reviewId: string,
        deleteAsset: boolean,
    ): Observable<ReviewAsset> {
        const url = `/api/review/${reviewId}/`;
        const options = {
            ids: [assetId],
            action: deleteAsset ? 'delete' : 'remove',
            withCredentials: true,
        };

        return this.http.patch(url, options).pipe(
            map((asset) => deserialize(ReviewAsset, asset)),
            switchMap(review => {
                if (review.child_ids.length === 0) {
                    review.children = [];
                    return of(review);
                }

                return this.assetService.resolve(review.child_ids)
                    .pipe(
                        map(assets => {
                            review.children = [];
                            for (const asset of assets) {
                                const index = review.child_ids.indexOf(asset.id);
                                review.children[index] = asset;
                            }
                            return review;
                        })
                    )
            }),
            tap((review) => {
                this._review = review;
                this.reviewSubject.next(review);
                this.reconcileAssetWatches(review);
                this.send({}, MessageType.ReviewChange);
            }),
        );
    }

    public reconnect(user: User) {
        this.websocketService.connect(user);
        this.reviewStateSubject.next(null);
    }

    public joinReview(): void {
        const data = {
            ReviewId: this._reviewId,
        };
        this.send(data, MessageType.ReviewJoin);
    }

    public leaveReview(): void {
        const data = {
            ReviewId: this._reviewId,
            Leave: true,
        };
        this.send(data, MessageType.ReviewJoin);

        this.requestSync(false);
        this.requestPresenter(false);

        // Stop watching any still-pending assets - we're not displaying this Review anymore, so we shouldn't
        // keep getting completion pushes for it. Rejoining re-derives the watch list from the fresh child list.
        for (const id of this._watchedAssetIds) {
            this.websocketService.send({Id: id, Unsubscribe: true}, MessageType.AssetSubscription);
        }
        this._watchedAssetIds.clear();
    }

    public requestSync(value: boolean): void {
        const data = {
            ReviewId: this._reviewId,
            RequestSync: value,
        };
        this.send(data, MessageType.ReviewSync);
        this.reviewVideoService.play(Source.All, false, Slot.Primary);
        this.reviewVideoService.play(Source.All, false, Slot.Compare);
    }

    public requestPresenter(value: boolean): void {
        const data = {
            ReviewId: this._reviewId,
            RequestPresenter: value,
        };
        this.send(data, MessageType.ReviewState);
        this.reviewVideoService.play(Source.All, false, Slot.Primary);
        this.reviewVideoService.play(Source.All, false, Slot.Compare);
    }

    public send(data: object, type: MessageType) {
        data['ReviewId'] = this._reviewId;
        this.websocketService.send(data, type);

        if (type === MessageType.ReviewState && this._reviewState.CompareLocked) {
            this.mirrorLockedFieldsLocally(data as Record<string, any>);
        }
    }

    // frog-ws mirrors a locked change onto the passive slot's fields and broadcasts that to every other
    // syncing member (ReviewController.cs §3.5) - but a ReviewState update is never echoed back to the
    // sender itself (WebSocketController sends it with sendToSelf: false), so without this, the client
    // actually driving the active slot never sees its own passive pane follow along while locked. This is
    // a local-only echo of the same mirroring the server already did for everyone else; the server stays
    // the source of truth once its (possibly adjusted, e.g. auto-unlock) broadcast comes back.
    private mirrorLockedFieldsLocally(data: Record<string, any>): void {
        const passiveSlot = this._reviewState.ActiveSlot === Slot.Primary ? Slot.Compare : Slot.Primary;

        if (data.Frame !== undefined || data.CompareFrame !== undefined) {
            this.reviewVideoService.setFrame(data.Frame ?? data.CompareFrame, Source.All, passiveSlot);
        }
        if (data.IsPlaying !== undefined || data.CompareIsPlaying !== undefined) {
            this.reviewVideoService.play(Source.All, data.IsPlaying ?? data.CompareIsPlaying, passiveSlot);
        }
        if (data.PlayRange !== undefined || data.ComparePlayRange !== undefined) {
            this.reviewVideoService.setRange(data.PlayRange ?? data.ComparePlayRange, Source.All, passiveSlot);
        }
        if (data.Transform !== undefined || data.CompareTransform !== undefined) {
            this.reviewImageService.setTransform(data.Transform ?? data.CompareTransform, passiveSlot);
        }
    }

    // Which slot (Primary/Compare) shared chrome - the timeline, mute/volume, annotation sidebar - should
    // currently target. See docs/design/review-compare-assets.md §3.4.
    public getActiveSlot(): Slot {
        return this._reviewState?.ActiveSlot ?? Slot.Primary;
    }

    // Sends a sparse ReviewState diff for whichever slot is currently active, remapping Frame/IsPlaying/
    // PlayRange/Transform to their Compare-prefixed equivalents when the Compare slot is active (§3.4 -
    // only the active slot's controls ever drive playback/transform; everything else is unprefixed).
    public sendActive(data: Record<string, any>): void {
        const remapped: Record<string, any> = {};
        for (const key of Object.keys(data)) {
            remapped[this._reviewState.activeFieldName(key)] = data[key];
        }
        this.send(remapped, MessageType.ReviewState);
    }

    public notifyReviewState(): void {
        this.reviewStateSubject.next(this._reviewState);
    }

    public setAssetBounds(assetBounds: AssetBounds): void {
        this.assetBoundsSubject.next(assetBounds);
    }

    // private webSocketOpen() {
    //     clearInterval(this._heartbeatTimer);
    //     this._heartbeatTimer = setInterval(() => {
    //         this._socket.send('1');
    //     }, 10 * 1000);
    // }

    private webSocketMessage(message: BaseMessage) {
        switch (message.Type) {
            case MessageType.ReviewChange:
                this.getReview(this._reviewId).subscribe({
                    error: (err) => Sentry.captureException(err),
                });
                break;
            case MessageType.AnnotationChange:
                this.annotationService.annotationsForAsset(this._reviewState.Asset);
                break;
            case MessageType.ReviewState:
                this.reviewStateHandler(message);
                break;
            case MessageType.AssetSubscription:
                this.assetSubscriptionHandler(message);
                break;
        }
    }

    // Status values are frogws.DTO.Status: Queued 0, Processing 1, Completed 2, Error 3. Note the response's
    // `Id` is the VideoQueue row's own id, not the Asset's - the Asset id comes back as `VideoId` (see
    // frogws.DTO.VideoQueueItem / VideoQueueRepository, which maps the `frog_video_videoqueue` table's `id`
    // and `video_id` columns respectively).
    private assetSubscriptionHandler(message: any): void {
        switch (message.Status) {
            case 2: // Completed - refresh so every subscribed client, not just whoever started the upload,
                    // picks up the now-ready Asset.
                this._watchedAssetIds.delete(message.VideoId);
                this.setAssetFailed(message.VideoId, false);
                this.getReview(this._reviewId).subscribe({
                    error: (err) => Sentry.captureException(err),
                });
                break;
            case 3: // Error - frog-ws already stopped polling (it drops every watcher on Status.Error), so
                    // there's nothing more to wait for. Stop tracking it locally too instead of leaving the
                    // "processing" placeholder spinning forever, and surface the failure.
                this._watchedAssetIds.delete(message.VideoId);
                this.setAssetFailed(message.VideoId, true);
                break;
            default:
                // Queued/Processing: a live update proves this asset isn't stuck failed anymore (e.g. someone
                // uploaded a fixed revision), so clear any stale failure marker.
                this.setAssetFailed(message.VideoId, false);
                break;
        }
    }

    private setAssetFailed(assetId: string, failed: boolean): void {
        const current = this.failedAssetIdsSubject.value;
        if (failed === current.has(assetId)) {
            return;
        }

        const next = new Set(current);
        if (failed) {
            next.add(assetId);
        } else {
            next.delete(assetId);
        }
        this.failedAssetIdsSubject.next(next);
    }

    // Subscribes to any not-ready child (still processing) that isn't already being watched, so completion is
    // pushed directly to this client instead of depending on the uploader's tab to relay it.
    private reconcileAssetWatches(review: ReviewAsset): void {
        const notReadyIds = new Set(
            (review.children || [])
                .filter((child) => child && child.is_ready === false)
                .map((child) => child.id),
        );

        for (const id of notReadyIds) {
            if (!this._watchedAssetIds.has(id)) {
                this._watchedAssetIds.add(id);
                this.websocketService.send({Id: id}, MessageType.AssetSubscription);
            }
        }

        for (const id of Array.from(this._watchedAssetIds)) {
            if (!notReadyIds.has(id)) {
                this._watchedAssetIds.delete(id);
            }
        }

        // Anything that's ready (or no longer even a child of this Review) can't still be in a failed state.
        for (const id of Array.from(this.failedAssetIdsSubject.value)) {
            if (!notReadyIds.has(id)) {
                this.setAssetFailed(id, false);
            }
        }
    }

    private reviewStateHandler(message: any) {
        this._reviewState = Object.assign(this._reviewState, message);
        this.reviewStateSubject.next(this._reviewState);

        for (const member of this._reviewState.Members) {
            this._userMap[member.UserId] = member;
        }

        const userMember = this._userMap[this._user.id];
        if (userMember) {
            const syncValue = userMember.IsSyncing ? SyncState.Syncing : SyncState.None;
            this.syncingStateSubject.next(syncValue);

            if (syncValue === SyncState.None) {
                // Dont apply any changes coming in
                return;
            }
        }

        // Update review video/image service with sparse data - Primary slot's fields update the Primary
        // slot's state, Compare slot's fields update the Compare slot's (see docs/design/review-compare-assets.md §3.2).
        if (message.Frame !== undefined) {
            this._reviewState.Frame = message.Frame;
            this.reviewVideoService.setFrame(message.Frame, Source.All, Slot.Primary);
        }

        if (message.IsPlaying !== undefined) {
            this.reviewVideoService.play(Source.All, message.IsPlaying, Slot.Primary);
        }

        if (message.Transform) {
            this.reviewImageService.setTransform(message.Transform, Slot.Primary);
        }

        if (message.PlayRange) {
            this.reviewVideoService.setRange(message.PlayRange, Source.All, Slot.Primary);
        }

        if (message.CompareFrame !== undefined) {
            this._reviewState.CompareFrame = message.CompareFrame;
            this.reviewVideoService.setFrame(message.CompareFrame, Source.All, Slot.Compare);
        }

        if (message.CompareIsPlaying !== undefined) {
            this.reviewVideoService.play(Source.All, message.CompareIsPlaying, Slot.Compare);
        }

        if (message.CompareTransform) {
            this.reviewImageService.setTransform(message.CompareTransform, Slot.Compare);
        }

        if (message.ComparePlayRange) {
            this.reviewVideoService.setRange(message.ComparePlayRange, Source.All, Slot.Compare);
        }

        if (message.RemoteStrokes) {
            for (const remoteStroke of message.RemoteStrokes) {
                this.stageService.updateRemoteStroke(remoteStroke);
            }
        }

        if (this._reviewState.Presenter === InvalidGuid) {
            this.presenterStateSubject.next(PresentingState.None);
        } else if (this._reviewState.Presenter === this._user.id) {
            this.presenterStateSubject.next(PresentingState.Self);
        } else {
            this.presenterStateSubject.next(PresentingState.Other);
        }
    }

    getUserById(userId: string): ReviewMember {
        return this._userMap[userId];
    }

    getLocalUser(): ReviewMember {
        return this.getUserById(this._user.id);
    }
}
